import type { ModuleRecord } from '@/types/domain';
import { priorityText } from './format';

/**
 * 各模块看板的维度定义。
 *
 * 抽出来是因为「画图」和「点图之后筛列表」必须用同一个取值口径——
 * 以前变更看板把这套逻辑写了两遍（组件里一遍、列表 matchesDrill 里一遍），
 * 只要改一处就会出现图上 7 条、点进去 5 条的情况。
 */
export interface DashboardDimension {
  kind: string;
  title: string;
  note?: string;
  limit?: number;
  /**
   * 只在跨项目时才有意义的维度（「按关联项目」这种）。
   * 在某个项目里打开时只会有一根柱子，纯占地方——按作用域过滤掉。
   */
  scope?: 'global';
  /**
   * labels 只有项目、变更类型两个维度用得上；其余维度不看它。
   * 返回空串 = 这条记录不进这张图（ModuleDashboard.tally 跳过，下钻也筛不到它）——
   * 积压 / 到期两张图只数未结束的，靠的就是这个（1028 Soft-Gate P2）。其余维度都自带「未填…」兜底。
   */
  read: (record: ModuleRecord, labels?: DashboardLabels) => string;
}

/**
 * 看板取值时借用的「字典」（1027 复评 r2）。
 *
 * 记录上常常只有 projectId、旧库类型 ID，名字在别处（项目清单、cfg_dictionaries）。
 * 画图和下钻拿的必须是同一份：只给画图不给下钻，图上叫「DH26003 · …」，
 * 点进去按旧名字筛，列表就是空的。
 */
export interface DashboardLabels {
  /** 项目内部 id（字符串键）→「项目号 · 名称」，和清单「关联项目」列同一份 */
  projects?: Record<string, string>;
  /** 变更类型 value（旧库 source_pk）→ 中文名，即 form-options 的 changeType */
  changeTypes?: Record<string, string>;
}

/** global=模块中心（跨项目）；project=某个项目的子页 */
export type DashboardScope = 'global' | 'project';

/** 点了图上某一根柱子之后，列表按这个筛 */
export interface DrillFilter { kind: string; value: string }

export interface DashboardConfig {
  /** 该模块是否有「要求完成日期」这个概念，没有就不提逾期 */
  tracksDue: boolean;
  /**
   * owner_name 这一列在本模块里到底是什么。
   * 人员模块把 email 映射到了 owner_name，说「未指定负责人」会看不懂。
   * 传 null 表示这列没有意义，不出这个 KPI。
   */
  ownerKpi: string | null;
  dimensions: DashboardDimension[];
}

/*
  各模块认定的「已结束」状态；库里中英文并存，两种都要认。

  0916 补 deleted / superseded：项目管理页顶上写着「未结束 30」，
  下面的列表却数出 12 条已结束——差的正是 11 个 deleted 的项目。
  同一页两个数字打架，比只有一个数字更难信。
  （库里 deleted 11 条、superseded 的审批单 13 条。）
*/
const TERMINAL = new Set([
  'closed', 'completed', 'approved', 'cancelled', 'obsolete', 'released', 'archived', 'inactive',
  'deleted', 'superseded',
  '已关闭', '已完成', '已取消', '已归档', '已批准', '已删除', '已作废',
]);

export function isTerminal(status: unknown): boolean {
  const raw = String(status ?? '').trim();
  return TERMINAL.has(raw) || TERMINAL.has(raw.toLowerCase());
}

function extra(record: ModuleRecord): Record<string, unknown> {
  return record.normalized && typeof record.normalized === 'object'
    ? record.normalized as Record<string, unknown>
    : {};
}

export function field(record: ModuleRecord, key: string): string {
  const value = record[key] ?? extra(record)[key];
  return value === null || value === undefined || value === '' ? '' : String(value);
}

/**
 * 负责人姓名。
 *
 * 文件管理里 owner_name 有两种存法：`["陈凯"]` 和 `陈凯`——
 * 旧库导出时一部分被当成 JSON 数组塞进来了。不拆开的话，
 * 同一个人会被算成两个人，「负责人 TOP」直接是错的。
 */
export function ownerName(record: ModuleRecord): string {
  const raw = field(record, 'owner_name') || field(record, 'ownerName') || field(record, 'owner_rname');
  const trimmed = raw.trim();
  if (!trimmed) return '未指定';
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed)) {
        const names = parsed.map((item) => String(item).trim()).filter(Boolean);
        return names.length ? names.join('、') : '未指定';
      }
    } catch {
      // 解析不了就按原样显示，至少不吞数据
    }
  }
  return trimmed;
}

/**
 * 「按关联项目」的柱子名，和清单「关联项目」列同一把尺子。
 *
 * 1027 复评 r2：图上「未命名项目 22」，同页清单却是 9 个具名项目——
 * 变更记录只带 projectId，号和名不在记录上，清单是拿 id 去项目清单里查的，图没有。
 * 所以先按 id 查 labels（和清单同一份），查不到再看记录自己带的号 / 名；
 * 都没有就写「未解析项目（#id）」：一个 id 一根柱子，查不到的也各是各的，
 * 不再整桶并成一根「未命名项目」。没挂项目的是「未关联项目」，另算。
 * 刻意不回落到 record.name：那是文件 / DFMEA 自己的名字，不是项目名。
 */
export function projectLabel(record: ModuleRecord, labels?: Record<string, string>): string {
  const linked = record.project_id ?? record.projectId;
  if (!linked) return '未关联项目';
  const id = String(linked);
  const known = labels?.[id];
  if (known) return known;
  const no = field(record, 'project_no') || field(record, 'projectNo');
  const name = field(record, 'project_name') || field(record, 'projectName');
  if (no && name) return `${no} · ${name}`;
  return name || no || `未解析项目（#${id}）`;
}

const LEGACY_ID = /^\d+(\.\d+)?$/;

/**
 * 「按变更类型」的柱子名：对照有效字典（form-options 的 changeType，旧库 source_pk → 中文名）。
 *
 * 迁移过来的 changeType / leixing 存的是旧库类型 ID（纯数字，或 1779943425.6386 这种小数时间戳）。
 * 字典命中用中文名；空的记「未填类型」；字典里查不到的 ID 记「未映射类型（原值）」——
 * 带着原值才对得回字典，也不会把几个不同的 ID 并成一根柱子。不是 ID 的原文照原样显示。
 */
export function changeTypeLabel(record: ModuleRecord, labels?: Record<string, string>): string {
  const raw = (field(record, 'changeType') || field(record, 'leixing')).trim();
  if (!raw) return '未填类型';
  const known = labels?.[raw] ?? sameLegacyId(raw, labels);
  if (known) return known;
  return LEGACY_ID.test(raw) ? `未映射类型（${raw}）` : raw;
}

/** 数值相等也算命中：JSON 里存成数字的 ID 转回字符串会丢小数末尾的 0 */
function sameLegacyId(raw: string, labels?: Record<string, string>): string | undefined {
  if (!labels || !LEGACY_ID.test(raw)) return undefined;
  const target = Number(raw);
  return Object.entries(labels).find(([key]) => LEGACY_ID.test(key) && Number(key) === target)?.[1];
}

export function dueDate(record: ModuleRecord): string {
  return (field(record, 'due_at') || field(record, 'dueAt')).slice(0, 10);
}

const today = (): string => new Date().toISOString().slice(0, 10);

export function isOverdue(record: ModuleRecord): boolean {
  const due = dueDate(record);
  return Boolean(due) && !isTerminal(record.status) && due < today();
}

/** 记录在手上放了多少天：提出日 → 关闭日，没关就算到今天 */
export function ageDays(record: ModuleRecord): number | null {
  // 旧库把提出时间存在 tctime，迁移后仍在 normalized 里
  const from = (field(record, 'tctime') || field(record, 'created_at')).slice(0, 10);
  if (!from) return null;
  const to = isTerminal(record.status) ? (field(record, 'updated_at').slice(0, 10) || today()) : today();
  const days = Math.floor((Date.parse(to) - Date.parse(from)) / 86_400_000);
  return Number.isFinite(days) ? Math.max(0, days) : null;
}

/*
  已结束的不进积压图（1028 Soft-Gate P2，复评步骤 02）：图上写着「只统计未结束的记录」，
  以前却单列一根「已结束 4」，和这句话自相矛盾。返回空串，看板就不数它。
*/
function ageBucket(record: ModuleRecord): string {
  if (isTerminal(record.status)) return '';
  const age = ageDays(record);
  if (age === null) return '无提出日期';
  if (age <= 7) return '0-7 天';
  if (age <= 30) return '8-30 天';
  if (age <= 60) return '31-60 天';
  return '超过 60 天';
}

export const AGE_BUCKETS = ['0-7 天', '8-30 天', '31-60 天', '超过 60 天'] as const;

const OWNER: DashboardDimension = {
  kind: 'owner', title: '按负责人（前 8）', limit: 8, read: ownerName,
};

const AGING: DashboardDimension = {
  kind: 'aging',
  title: '按积压时长',
  note: '只统计未结束的记录',
  read: ageBucket,
};

const PROJECT: DashboardDimension = {
  kind: 'project',
  title: '按关联项目（前 10）',
  scope: 'global',
  read: (record, labels) => projectLabel(record, labels?.projects),
};

/**
 * 交付物的「到期」分桶。
 *
 * 不复用 AGING：那个算的是「提出到现在放了多久」，读的是 tctime / created_at，
 * 而交付物接口给的是 camelCase 的 createdAt——直接套过来每一条都会落进「无提出日期」。
 * 交付物真正要看的也不是积压天数，是**还有几天到期**。
 * 已结束的同积压图一样不计入（1028 Soft-Gate P2）：note 写的就是「只统计未结束的交付物」。
 */
function deliverableDueBucket(record: ModuleRecord): string {
  if (isTerminal(record.status)) return '';
  const due = dueDate(record);
  if (!due) return '未设期限';
  const days = Math.floor((Date.parse(due) - Date.parse(today())) / 86_400_000);
  if (!Number.isFinite(days)) return '未设期限';
  if (days < 0) return '已逾期';
  if (days <= 7) return '7 天内到期';
  if (days <= 30) return '30 天内到期';
  return '30 天以上';
}

export const DELIVERABLE_DUE_BUCKETS = ['已逾期', '7 天内到期', '30 天内到期', '30 天以上'] as const;

/**
 * 交付物的审批口径，和列表里的 approvalLabel 必须是同一把尺子。
 *
 * 生产库里有 23 条自身状态是 approved/rejected、approval_status 却是 not_submitted——
 * 全部来自 legacy_eqs_206（旧 EQS 有结论，新系统的审批实例表里没有对应记录）。
 * 按 approval_status 老实分桶的话，这 23 条会被算进「未提交」，
 * 图上就会说「一条都没批过」，而列表里明明写着已批准。单开一桶，不假装它们没批过，
 * 也不回填 approval_status 伪造审计痕迹。
 */
function deliverableApprovalBucket(record: ModuleRecord): string {
  const approval = field(record, 'approvalStatus') || field(record, 'approval_status') || 'not_submitted';
  const own = String(record.status ?? '');
  if (approval === 'not_submitted' && (own === 'approved' || own === 'rejected')) {
    return '旧系统结论';
  }
  return ({
    not_submitted: '未提交审批',
    pending: '待审批',
    approved: '审批通过',
    rejected: '已退回',
  } as Record<string, string>)[approval] ?? approval;
}

/**
 * 逐模块配置。
 *
 * 维度是照着库里真实取值挑的，不是照字段清单猜的：
 * - 变更的 changeSource 19 条全空，所以不做「按来源」；
 * - 文件没有 category / responsible_department 这两列，只有 normalized.type；
 * - DFMEA 只有 4 份文件、0 条失效条目，能画的只有状态；
 * - 人员走的是 iam_users，接口没有映射 normalized，公司/组织名根本取不到，
 *   所以画的是「邮箱能不能用」「工号是不是新制」这些真拿得到又真要紧的。
 */
const CONFIG: Readonly<Record<string, DashboardConfig>> = {
  projects: {
    tracksDue: false,
    ownerKpi: null,
    dimensions: [
      {
        kind: 'priority',
        title: '按优先级',
        // 0916 库里统一成 critical/high/normal/low；图上翻成中文，别把英文码画出来
        read: (record) => priorityText(field(record, 'priority')) || '未填优先级',
      },
      { kind: 'customer', title: '按客户（前 10）', read: (record) => field(record, 'customer') || '未填客户' },
      { kind: 'owner', title: '按项目经理（前 8）', limit: 8, read: (record) => field(record, 'project_manager') || field(record, 'projectManager') || ownerName(record) },
    ],
  },
  dfmea: {
    tracksDue: false,
    ownerKpi: '未指定负责人',
    dimensions: [PROJECT, OWNER],
  },
  'pfmea-cp': {
    tracksDue: false,
    ownerKpi: '未指定负责人',
    dimensions: [PROJECT, OWNER],
  },
  files: {
    tracksDue: false,
    ownerKpi: '未指定负责人',
    dimensions: [
      {
        kind: 'doctype',
        title: '按记录类型',
        note: '文件夹也占一行；「文件总数」里含文件夹',
        read: (record) => {
          const type = field(record, 'type') || field(record, 'documentType');
          if (type === 'folder') return '文件夹';
          if (type === 'file') return '文件';
          return '未标记类型';
        },
      },
      OWNER,
      PROJECT,
    ],
  },
  issues: {
    tracksDue: true,
    ownerKpi: '未指定责任人',
    dimensions: [
      {
        kind: 'grade',
        title: '按问题等级',
        note: '取 A/B/C/D 等级，不回落到优先级——那是另一把尺子',
        read: (record) => field(record, 'severityGrade') || '未定级',
      },
      { kind: 'category', title: '按问题类型', read: (record) => field(record, 'category') || '未分类' },
      OWNER,
      AGING,
    ],
  },
  changes: {
    tracksDue: true,
    ownerKpi: '未指定牵头人',
    dimensions: [
      {
        kind: 'changetype',
        title: '按变更类型',
        /*
          1027 r2：按变更表单下拉那一份字典翻中文（见 changeTypeLabel），不再只改兜底字样。
          1028 Soft-Gate P2：图上不再挂「字典 / 旧库 ID」这类实现说明，标题已经说清按什么分；
          字典查不到的旧库 ID 仍显示「未映射类型（原值）」，柱子名自己会说明。
        */
        read: (record, labels) => changeTypeLabel(record, labels?.changeTypes),
      },
      {
        kind: 'source',
        title: '按变更来源',
        // 新字段叫 changeSource 但 19 条全空，实际数据在旧库字段 laiyuan 里
        read: (record) => field(record, 'changeSource') || field(record, 'laiyuan') || '未填来源',
      },
      PROJECT,
      OWNER,
      AGING,
    ],
  },
  hr: {
    tracksDue: false,
    // iam_users 把 email 映射到了 owner_name，说「未指定负责人」会让人看不懂
    ownerKpi: '未维护邮箱',
    dimensions: [
      {
        kind: 'mail',
        title: '按邮箱可用性',
        note: '脱敏占位的收不到通知——这是外发提醒开不了的原因',
        read: (record) => {
          const mail = (field(record, 'owner_name') || field(record, 'ownerName')).trim();
          if (!mail) return '未维护邮箱';
          // 迁移过来的联系方式被打成 x***@ 这种占位符，发不出去
          return /\*{2,}/u.test(mail) ? '脱敏占位' : '可用邮箱';
        },
      },
      {
        kind: 'account',
        title: '按账号形态',
        note: '钉钉按工号对人，旧的点号账号对不上',
        read: (record) => {
          const account = field(record, 'code').trim();
          if (!account) return '未维护账号';
          return account.includes('.') ? '旧点号账号' : '工号账号';
        },
      },
      {
        kind: 'org',
        title: '按组织归属',
        note: '接口只给组织 ID，给不了组织名，这里只能看有没有挂',
        read: (record) => (record.parent_id ?? record.parentId ? '已挂组织' : '未挂组织'),
      },
    ],
  },
  /*
    交付物只在项目页里出现（模块中心没有这个模块），所以维度全部按「在一个项目里看」挑：
    - 审批状态：门禁 2026-08 上线后最该盯的一列，口径和列表的 approvalLabel 对齐；
    - 有没有正文文件：没有文件的交付物提交不了审批，也没法当评审证据——
      这是「完成率 1/122」背后最先要问的问题，比状态分布更能指出下一步做什么；
    - 挂在哪个计划节点：交付物是挂节点的，按节点看才知道哪一段没人交东西；
    - 负责人 / 到期：谁欠着、什么时候到期。
    刻意不做「按阶段」：接口只给 node_name，阶段要从节点父级 P0x 推，这里拿不到——
    宁可不做，也不按名字猜阶段。
  */
  deliverables: {
    tracksDue: true,
    ownerKpi: '未指定负责人',
    dimensions: [
      {
        kind: 'approval',
        title: '按审批状态',
        note: '「旧系统结论」是旧 EQS 批过、新系统没有审批记录的那批，不算进未提交',
        read: deliverableApprovalBucket,
      },
      {
        kind: 'evidence',
        title: '有没有正文文件',
        note: '没有文件就提交不了审批，评审也没有证据可看',
        read: (record) => (record.documentId ?? record.document_id ? '已上传正文' : '尚无文件'),
      },
      {
        kind: 'node',
        title: '按计划节点（前 10）',
        read: (record) => field(record, 'nodeName') || field(record, 'node_name') || '未挂节点',
      },
      OWNER,
      { kind: 'due', title: '按到期情况', note: '只统计未结束的交付物', read: deliverableDueBucket },
    ],
  },
  // 系统设置不做看板：25 条模板全是 active、版本全是 legacy，
  // 三张图就是三根一样长的柱子，占地方还让人以为有东西可看。
};

export function dashboardConfig(
  moduleKey: string,
  scope: DashboardScope = 'global',
): DashboardConfig | null {
  const config = CONFIG[moduleKey] ?? null;
  if (!config || scope === 'global') return config;
  const dimensions = config.dimensions.filter((dimension) => dimension.scope !== 'global');
  // 跨项目维度剔完什么都不剩，就别出这块图了——只剩「按状态」一张不值当占一屏
  return dimensions.length ? { ...config, dimensions } : null;
}

/**
 * 图上点了哪块，列表就留哪些——和画图共用一个 read。
 * labels 要传画图时那一份（项目名、变更类型字典），否则图上按名字分、这里按 ID 兜底，对不上。
 */
export function matchesDrill(
  moduleKey: string,
  record: ModuleRecord,
  filter: DrillFilter | null,
  labels?: DashboardLabels,
): boolean {
  if (!filter) return true;
  if (filter.kind === 'status') return String(record.status ?? '') === filter.value;
  // 这里刻意用全量配置：作用域只决定「画不画这张图」，不该影响已经点下去的筛选怎么算
  const dimension = dashboardConfig(moduleKey)?.dimensions
    .find((item) => item.kind === filter.kind);
  return dimension ? dimension.read(record, labels) === filter.value : true;
}
